// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import parseQueryString from "@insite/client-framework/Common/Utilities/parseQueryString";
import { trackAutocompleteSearchResultEvent } from "@insite/client-framework/Common/Utilities/tracking";
import {
    addSearchHistory,
    getSearchHistory,
    SearchHistoryItem,
} from "@insite/client-framework/Services/AutocompleteService";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import getAutocompleteModel from "@insite/client-framework/Store/CommonHandlers/GetAutocompleteModel";
import loadRealTimePricing from "@insite/client-framework/Store/CommonHandlers/LoadRealTimePricing";
import sendSearchTracking from "@insite/client-framework/Store/CommonHandlers/SendSearchTracking";
import loadProductInfoList from "@insite/client-framework/Store/Components/ProductInfoList/Handlers/LoadProductInfoList";
import { getProductsForProductInfoList } from "@insite/client-framework/Store/Components/ProductInfoList/ProductInfoListSelectors";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import {
    AutocompleteItemModel,
    AutocompleteModel,
    ProductAutocompleteItemModel,
    ProductModel,
} from "@insite/client-framework/Types/ApiModels";
import Button, { ButtonPresentationProps, ButtonWrapper } from "@insite/mobius/Button";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import { StyledClickable } from "@insite/mobius/Link";
import TextField, { TextFieldPresentationProps } from "@insite/mobius/TextField";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import { getProp } from "@insite/mobius/utilities";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import getColor from "@insite/mobius/utilities/getColor";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import { McMcHeaderContext } from "@mcmc/App/Pages/Header/HeaderContext";
import { isUnderCheckoutFlow } from "@mcmc/App/Store/Selectors/PageSelectors";
import { getSearchType } from "@mcmc/App/Store/Selectors/SearchResultsSelectors";
import { getSettingsCollection } from "@mcmc/App/Store/Selectors/SettingsSelectors";
import AutocompleteBrands, { AutocompleteBrandsStyles } from "@mcmc/Overrides/Widgets/Header/AutocompleteBrands";
import AutocompleteCategories, {
    AutocompleteCategoriesStyles,
} from "@mcmc/Overrides/Widgets/Header/AutocompleteCategories";
import AutocompleteContent, { AutocompleteContentStyles } from "@mcmc/Overrides/Widgets/Header/AutocompleteContent";
import AutocompleteProducts, { AutocompleteProductsStyles } from "@mcmc/Overrides/Widgets/Header/AutocompleteProducts";
import { AutocompleteProductTileModel } from "@mcmc/Overrides/Widgets/Header/AutocompleteProductTile";
import SearchHistory, { SearchHistoryStyles } from "@mcmc/Overrides/Widgets/Header/SearchHistory";
import EllipsisSpinner from "@mcmc/XDL/Components/Common/EllipsisSpinner";
import { layoutContainer, styledScrollbar } from "@mcmc/XDL/Utilities";
import mergeToNew from "@mcmc/XDL/Utilities/xMergeToNew";
import cloneDeep from "lodash/cloneDeep";
import debounce from "lodash/debounce";
import throttle from "lodash/throttle";
import React from "react";
import { connect, ResolveThunks } from "react-redux";
import styled, { css, ThemeProps, withTheme } from "styled-components";

// eslint-disable-next-line spire/fenced-imports
import Tippy, { TippyProps } from "@tippyjs/react";
// eslint-disable-next-line spire/fenced-imports
import sendAutocompleteOdpEvent from "@insite/client-framework/Store/CommonHandlers/SendAutocompleteOdpEvent";
import AutocompleteSuggestions, {
    AutocompleteSuggestionsStyles,
} from "@insite/content-library/Widgets/Header/AutocompleteSuggestions";
// eslint-disable-next-line spire/fenced-imports
import { Instance as TippyInstance } from "tippy.js";

interface CustomAutocompleteModel extends AutocompleteModel {
    trainings: AutocompleteItemModel[] | null;
    productTiles: AutocompleteProductTileModel[];
}

interface OwnProps {
    /** A unique identifier to assist with id-generation for `aria-controls`. */
    readonly id: string;
    inputRef?: React.Ref<HTMLInputElement>;
    onBeforeGoToUrl?: () => void;
    extendedStyles?: SearchInputStyles;
    isAnimated?: boolean;
}

const PopoverTrigger = styled.div.attrs({ tabIndex: 0 })<InjectableCss<any>>`
    visibility: hidden;
    position: absolute;
    height: 40px;
    width: 50px;
`;

const mapStateToProps = (state: ApplicationState) => {
    const {
        searchSettings: { autocompleteEnabled, searchHistoryEnabled, searchHistoryLimit, searchPath },
        customSettings: { autoCompleteProductLimit, rootTrainingAndEventsCategoryUrl },
        productSettings: { catalogUrlPath },
    } = getSettingsCollection(state);

    return {
        autocompleteEnabled,
        searchHistoryEnabled,
        searchHistoryLimit,
        autoCompleteProductLimit: Number(autoCompleteProductLimit),
        trainingListPageUrl: `${catalogUrlPath}${rootTrainingAndEventsCategoryUrl}`,
        categoryListPageUrl: getPageLinkByPageType(state, "CategoryListPage")?.url,
        brandsPageUrl: getPageLinkByPageType(state, "BrandsPage")?.url,
        location: getLocation(state),
        searchPath,
        appState: state,
        searchType: getSearchType(state),
        disableAddToCartActions: isUnderCheckoutFlow(state),
    };
};

const mapDispatchToProps = {
    getAutocompleteModel,
    sendSearchTracking,
    sendAutocompleteOdpEvent,
    // XNG-Change
    loadRealTimePricing,
    loadProductInfoList,
};

type Props = OwnProps &
    ThemeProps<BaseTheme> &
    HasHistory &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps>;

interface State {
    query: string;
    focusableAutocompleteItems: AutocompleteItemModel[];
    focusedAutocompleteItem?: AutocompleteItemModel;
    focusedSearchHistoryQuery?: string;
    autoCompleteModel?: CustomAutocompleteModel;
    isLoading: boolean;
    clickedProduct: boolean;
    // XNG-Change
    selectedCategoryId: string | null;
    isEvents: boolean;
}

const DOWN_KEY = 40;
const UP_KEY = 38;
const ENTER_KEY = 13;

export interface SearchInputStyles {
    wrapper?: InjectableCss;
    input?: TextFieldPresentationProps;
    popoverContent?: InjectableCss;
    popoverContentBody?: InjectableCss;
    autocompleteWrapper?: InjectableCss;
    autocompleteColumnWrapper?: InjectableCss;
    autocompleteCommerceSearchWrapper?: InjectableCss;
    autocompleteProductColumn?: InjectableCss;
    autocompleteFullProductColumn?: InjectableCss;
    searchHistoryStyles?: SearchHistoryStyles;
    autocompleteSuggestionsStyles?: AutocompleteSuggestionsStyles;
    autocompleteCategoriesStyles?: AutocompleteCategoriesStyles;
    autocompleteBrandsStyles?: AutocompleteBrandsStyles;
    autocompleteContentStyles?: AutocompleteContentStyles;
    autocompleteProductsStyles?: AutocompleteProductsStyles;
    noResultsMessage?: TypographyPresentationProps;
    searchButton?: ButtonPresentationProps;
}

export const searchInputStyles: SearchInputStyles = {
    wrapper: {
        css: css`
            display: flex;

            .tippy-box {
                outline: none;
                ${StyledClickable}:focus,${ButtonWrapper}:focus {
                    outline-color: ${getProp("theme.focus.color", "#09f")};
                }
            }
        `,
    },
    input: {
        sizeVariant: "default",
        cssOverrides: {
            inputSelect: css`
                ${getProp("theme.formField.defaultProps.cssOverrides.inputSelect")}
                background-color: ${getColor("text.inverse")};
            `,
        },
    },
    popoverContent: {
        css: css`
            max-height: calc(100vh - 200px);
            overflow: auto;
            ${styledScrollbar()}
            border: 1px solid ${getColor("common.border")};
            border-top: 3px solid ${getColor("primary")};
            background-color: ${getColor("text.inverse")};
            overscroll-behavior: contain;
            .autocomplete-tippy-arrow {
                display: block;
                width: 15px;
                border: 10px solid transparent;
                border-bottom: 10px solid ${getColor("primary")};
                top: -18px;
            }

            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        css`
                            max-height: calc(100vh - 250px);
                        `,
                    ],
                    "max",
                )}
        `,
    },
    popoverContentBody: {
        css: css`
            ${layoutContainer}
            padding-top: 20px;
            padding-bottom: 20px;
        `,
    },
    autocompleteWrapper: {
        css: css`
            display: flex;
            flex-direction: row;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        css`
                            flex-direction: column;
                        `,
                    ],
                    "max",
                )}
        `,
    },
    autocompleteColumnWrapper: {
        css: css`
            flex: 0 0 325px;
            max-width: 325px;
            padding: 0 20px 0 0;
            border-right: 1px solid ${getColor("common.border")};
            align-items: flex-start;

            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        css`
                            flex: 1 1 auto;
                            padding: 0;
                            border-right: none;
                            max-width: none;
                        `,
                    ],
                    "max",
                )}
        `,
    },
    autocompleteProductColumn: {
        css: css`
            padding: 0 0 0 20px;
            flex: 1 1 100%;

            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        css`
                            padding: 20px 0 0 0;
                        `,
                    ],
                    "max",
                )}
        `,
    },
    autocompleteFullProductColumn: {
        css: css`
            flex: 1 1 100%;
        `,
    },
    noResultsMessage: {
        size: 22,
        weight: 700,
        color: "common.ui3",
        css: css`
            min-height: 50px;
            height: 100%;
            display: flex;
            align-items: center;
            justify-content: center;
            margin-bottom: 0;
        `,
    },
    searchButton: {
        typographyProps: { size: 14, weight: 700 },
        css: css`
            margin: 0;
        `,
    },
};

const tippyProps: TippyProps = {
    maxWidth: "none",
    hideOnClick: false,
    trigger: "manual",
    placement: "bottom-start",
    interactive: true,
    animation: false,
    duration: [0, 0],
    popperOptions: {
        modifiers: [
            {
                name: "preventOverflow",
                options: {
                    padding: 0,
                },
            },
            {
                name: "arrow",
                options: {
                    padding: 0,
                    element: "#search-arrow",
                },
            },
        ],
    },
};

const styles = searchInputStyles;

let autoCompleteRequestTimestamp: number;
let realTimeRequestTimestamp: number;

class SearchInput extends React.Component<Props, State> {
    private readonly styles: SearchInputStyles;
    private tippyInstance: TippyInstance | undefined;
    private hoverTimeoutId: number | undefined;
    static contextType = McMcHeaderContext;

    constructor(props: Props) {
        super(props);

        this.styles = mergeToNew(styles, props.extendedStyles);
        const parsedQuery = parseQueryString<{ query?: string }>(this.props.location.search);
        this.state = {
            query: parsedQuery.query || "",
            focusableAutocompleteItems: [],
            isLoading: false,
            clickedProduct: true,
            // XNG-Change
            selectedCategoryId: null,
            isEvents: false,
        };
        this.loadCategoryProducts = this.loadCategoryProducts.bind(this);
        this.unloadCategoryProducts = this.unloadCategoryProducts.bind(this);
        this.onMouseEnterProductPanel = this.onMouseEnterProductPanel.bind(this);
    }

    showAutocomplete = true;

    componentDidUpdate(prevProps: Props, prevState: State) {
        if (this.props.location.pathname !== prevProps.location.pathname) {
            // clear the search box on navigate away from search
            if (!this.props.location.pathname.toLowerCase().startsWith(`/${this.props.searchPath.toLowerCase()}`)) {
                // TODO ISC-12679 - Redesign so that this can be moved to a field of the class rather than state.
                // eslint-disable-next-line react/no-did-update-set-state
                this.setState({ query: "" });
            }
            this.closeAutocomplete();
        }
        if (
            this.state.autoCompleteModel &&
            prevState.autoCompleteModel &&
            this.state.autoCompleteModel !== prevState.autoCompleteModel
        ) {
            this.updateFocusableItems();
        }
    }

    componentDidMount() {}

    componentWillUnmount() {}

    updateFocusableItems = () => {
        if (this.state.isLoading || !this.state.autoCompleteModel) {
            return;
        }

        const { categories, brands, content, productTiles, trainings } = this.state.autoCompleteModel;
        const newFocusableOptions = (categories || [])
            .concat(trainings || [])
            .concat(brands || [])
            .concat(content || [])
            .concat(productTiles || []);
        this.setState({
            focusableAutocompleteItems: newFocusableOptions,
            focusedAutocompleteItem: undefined,
            focusedSearchHistoryQuery: undefined,
        });
    };

    doSearch = (resultsSectionId?: string) => {
        if (this.state.query.trim() === "") {
            return;
        }

        this.showAutocomplete = false;
        if (this.state.autoCompleteModel?.products?.length === 1) {
            this.goToUrl(this.state.autoCompleteModel?.products[0].url);
        } else {
            this.goToUrl(
                `/${this.props.searchPath}?query=${this.state.query}&searchType=${
                    resultsSectionId || this.props.searchType
                }`,
                false,
            );
            if (this.props.location.pathname.toLowerCase().startsWith(`/${this.props.searchPath.toLowerCase()}`)) {
                // if we already on the Search page then scroll to top
                window.scrollTo(0, 0);
            }
        }
        if (this.props.searchHistoryEnabled) {
            addSearchHistory(this.state.query, this.props.searchHistoryLimit);
        }
    };

    loadRealtimePrices = (autocompleteResult: CustomAutocompleteModel) => {
        if (autocompleteResult?.products?.length) {
            const currentRequestTimestamp = (realTimeRequestTimestamp = new Date().getTime());
            this.props.loadRealTimePricing({
                productPriceParameters: autocompleteResult.products
                    .filter(product => product.properties.isEvent?.toLowerCase() !== "true")
                    .map(product => ({
                        productId: product.id || "",
                        unitOfMeasure: product.properties.uom || "",
                        qtyOrdered: Number(product.properties.minQty) || 1,
                    })),
                onSuccess: result => {
                    if (currentRequestTimestamp !== realTimeRequestTimestamp) {
                        return;
                    }
                    this.setState(current => {
                        result.realTimePricingResults?.forEach(pricing => {
                            const productTileData = current.autoCompleteModel?.productTiles?.find(
                                ({ productInfo }) => productInfo?.productId === pricing.productId,
                            );
                            if (productTileData && productTileData.productInfo) {
                                productTileData.productInfo.pricing = pricing;
                                delete productTileData.productInfo.failedToLoadPricing;
                            }
                        });
                        return {
                            autoCompleteModel: {
                                ...autocompleteResult,
                                productTiles: cloneDeep(autocompleteResult.productTiles),
                            },
                        };
                    });
                },
                onError: result => {
                    if (currentRequestTimestamp !== realTimeRequestTimestamp) {
                        return;
                    }
                    this.setState(current => {
                        current.autoCompleteModel?.productTiles?.every(({ productInfo }) => {
                            if (productInfo) {
                                productInfo.failedToLoadPricing = true;
                                delete productInfo.pricing;
                            }

                            return true;
                        });
                        return {
                            autoCompleteModel: {
                                ...autocompleteResult,
                                productTiles: cloneDeep(autocompleteResult.productTiles),
                            },
                        };
                    });
                },
            });
        }
    };

    searchClickHandler = () => {
        this.doSearch();
    };

    loadAutocomplete = debounce((query: string) => {
        const currentRequestTimestamp = (autoCompleteRequestTimestamp = new Date().getTime());
        this.props.autocompleteEnabled &&
            this.props.getAutocompleteModel({
                query,
                onSuccess: result => {
                    if (currentRequestTimestamp !== autoCompleteRequestTimestamp) {
                        return;
                    }
                    let customResult: CustomAutocompleteModel;
                    if (this.showAutocomplete) {
                        const productTiles: AutocompleteProductTileModel[] = [];
                        const trainings: AutocompleteItemModel[] = [];
                        if (result?.products?.length) {
                            result.products.every((product: ProductAutocompleteItemModel) => {
                                if (product.properties.isEvent?.toLowerCase() === "true") {
                                    trainings.push(product);
                                } else {
                                    const productTile: AutocompleteProductTileModel = {
                                        product: {
                                            id: product.id,
                                            productNumber: product.erpNumber,
                                            mediumImagePath: product.image,
                                            productTitle: product.title,
                                            canonicalUrl: product.url,
                                            canAddToCart: product.properties.canAddToCart === "True",
                                            quoteRequired: product.properties.quoteRequired === "True",
                                            manufacturerItem: product.manufacturerItemNumber,
                                            minimumOrderQty: Number(product.properties.minimumOrderQty) || 1,
                                            brand: {
                                                name: product.brandName,
                                            },
                                            unitOfMeasures: [
                                                {
                                                    unitOfMeasure: product.properties.uom,
                                                    unitOfMeasureDisplay: product.properties.uom,
                                                },
                                            ],
                                        } as ProductModel,
                                        productInfo: {
                                            productId: product.id || "",
                                            qtyOrdered: Number(product.properties.minimumOrderQty) || 1,
                                            unitOfMeasure: product.properties.uom || "",
                                            productDetailPath: product.url,
                                        },
                                    } as AutocompleteProductTileModel;
                                    productTile.onQtyOrderedChanged = (qty: number) => {
                                        this.setState(current => {
                                            if (productTile.productInfo) {
                                                productTile.productInfo.qtyOrdered = qty;
                                            }
                                            return {
                                                autoCompleteModel: {
                                                    ...current.autoCompleteModel,
                                                    productTiles: cloneDeep(customResult.productTiles),
                                                },
                                            } as State;
                                        });
                                    };
                                    productTiles.push(productTile);
                                }
                                return true;
                            });
                            customResult = {
                                ...result,
                                trainings,
                                productTiles,
                            };
                        } else {
                            customResult = {
                                ...result,
                                trainings: [],
                                productTiles: [],
                            };
                        }

                        this.setState({
                            autoCompleteModel: customResult,
                        });
                        this.updatePopoverVisibility(customResult);
                        this.loadRealtimePrices(customResult);
                    }
                },
                onComplete(resultProps) {
                    if (resultProps.apiResult) {
                        this.onSuccess?.(resultProps.apiResult);
                    }
                },
            });
    }, 200);

    isPopoverVisible(results: CustomAutocompleteModel | undefined) {
        let isVisible: boolean | null = false;
        if (this.state.query && this.state.query.length > 2 && results) {
            const { categories, brands, content, products, trainings } = results;
            isVisible =
                this.props.autocompleteEnabled &&
                ((categories && categories.length > 0) ||
                    (trainings && trainings.length > 0) ||
                    (brands && brands.length > 0) ||
                    (content && content.length > 0) ||
                    (products && products.length > 0));
        } else if (!this.state.query) {
            const searchHistoryItems = getSearchHistory();
            isVisible = this.props.searchHistoryEnabled && searchHistoryItems && searchHistoryItems.length > 0;
        }
        return isVisible;
    }

    updatePopoverVisibility(results: CustomAutocompleteModel | undefined) {
        if (this.isPopoverVisible(results)) {
            this.openAutocomplete();
        } else {
            this.closeAutocomplete();
        }
    }

    handleInputFocus = (event: React.FocusEvent<HTMLInputElement>) => {
        this.handleInputChange(event);
    };

    handleInputChange = (event: React.ChangeEvent<HTMLInputElement>, toggleAutoComplete = true) => {
        this.showAutocomplete = toggleAutoComplete;
        const newQuery = event.target.value;
        this.loadAutocomplete(newQuery);
        this.setState({ query: newQuery, selectedCategoryId: null, isEvents: false }, () => {
            this.updatePopoverVisibility(this.state.autoCompleteModel);
        });
    };

    openAutocomplete = () => {
        this.tippyInstance?.show();
    };

    closeAutocomplete = () => {
        this.tippyInstance?.hide();
    };

    moveFocus = throttle((direction: "prev" | "next") => {
        if (this.state.query) {
            this.setState(({ focusedAutocompleteItem, focusableAutocompleteItems }) => {
                const focusableQueue =
                    direction === "prev" ? focusableAutocompleteItems.slice().reverse() : focusableAutocompleteItems;
                const focusedIndex = focusableQueue.indexOf(focusedAutocompleteItem as AutocompleteItemModel);
                const newFocusedOption = focusableQueue.find((_, index) => index > focusedIndex);
                return { focusedAutocompleteItem: newFocusedOption };
            });
        } else {
            this.setState(({ focusedSearchHistoryQuery }) => {
                const searchHistoryItems = getSearchHistory();
                const focusableQueue = direction === "prev" ? searchHistoryItems.slice().reverse() : searchHistoryItems;
                const focusedSearchHistoryItem = focusableQueue.find(o => o.query === focusedSearchHistoryQuery);
                const focusedIndex = focusableQueue.indexOf(focusedSearchHistoryItem as SearchHistoryItem);
                const newFocusedOption = focusableQueue.find((_, index) => index > focusedIndex);
                return { focusedSearchHistoryQuery: newFocusedOption?.query };
            });
        }
    }, 100);

    handlePopoverKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
        switch (event.keyCode) {
            case DOWN_KEY:
                this.moveFocus("next");
                break;
            case UP_KEY:
                this.moveFocus("prev");
                break;
            case ENTER_KEY:
                if (this.state.focusedAutocompleteItem) {
                    this.goToUrl(this.state.focusedAutocompleteItem.url);
                } else if (this.state.focusedSearchHistoryQuery) {
                    this.setState({ query: this.state.focusedSearchHistoryQuery }, () => {
                        this.doSearch();
                    });
                } else {
                    this.doSearch();
                }
                break;
            default:
                break;
        }
    };

    onClearHistory = () => {
        this.closeAutocomplete();
    };

    onClickOutside = (instance: TippyInstance, event: Event) => {
        const target: any = event.target;
        if (!target || target.id !== `headerSearch-${this.props.id}`) {
            this.closeAutocomplete();
        }
    };

    handlePopoverClose = () => {
        if (!this.state.clickedProduct && (this.state.autoCompleteModel?.products?.length ?? 0) > 0) {
            this.props.sendAutocompleteOdpEvent({});
        }
        this.setState({
            clickedProduct: false,
        });
        this.setState({
            focusedAutocompleteItem: undefined,
            focusedSearchHistoryQuery: undefined,
        });
    };

    trackAutcompleteSearchResults = () => {
        // Make sure only to trigger for autocomplete results
        if (!this.state.query || !this.props.autocompleteEnabled || !this.state.autoCompleteModel) {
            return;
        }

        const {
            categories,
            brands,
            content,
            products,
            suggestions,
            popularBrands,
            popularCategories,
            isRetailSearchCompletionResults,
        } = this.state.autoCompleteModel;

        const resultCount =
            (products?.length ?? 0) + (categories?.length || 0) + (content?.length || 0) + (brands?.length || 0);
        if (resultCount > 0) {
            // Track an autocomplete of 1+ items
            this.props.sendSearchTracking({
                searchEvent: "autocompleteSearchResults",
                searchTerm: this.state.query,
                resultCount,
                productCount: products?.length,
                categoryCount: categories?.length,
                contentCount: content?.length,
                brandCount: brands?.length,
            });
            return;
        }

        // Track zero resultCount search results and triggers from this.doSearch
        this.props.sendSearchTracking({
            searchEvent: "searchResults",
            searchTerm: this.state.query,
            resultCount,
        });
    };

    goToUrl = (url: string, track = true) => {
        const data = {
            searchQuery: this.state.query,
            searchPosition:
                this.state.autoCompleteModel?.suggestions?.findIndex(o => o.url === url) ??
                this.state.autoCompleteModel?.products?.findIndex(o => o.url === url) ??
                -1,
            attributionToken: this.state.autoCompleteModel?.attributionToken,
        };
        if (data.searchPosition >= 0) {
            this.props.sendAutocompleteOdpEvent(data);
            this.setState({ clickedProduct: true });
        }

        this.props.onBeforeGoToUrl && this.props.onBeforeGoToUrl();
        if (track) {
            this.trackAutcompleteSearchResults();
        }
        this.closeAutocomplete();
        this.setState({
            // don't use stale results on next search and go to wrong PDP
            autoCompleteModel: undefined,
        });
        this.props.history.push(url);
    };

    loadCategoryProducts(category: AutocompleteItemModel, isEvents: boolean) {
        clearTimeout(this.hoverTimeoutId);
        this.hoverTimeoutId = setTimeout(() => {
            if (category.id && this.state.selectedCategoryId !== category.id) {
                const selectedCategoryProducts = getProductsForProductInfoList(
                    this.props.appState,
                    `search-autoComplete-${category.id}-${this.state.query}`,
                );
                if (!selectedCategoryProducts) {
                    this.props.loadProductInfoList({
                        id: `search-autoComplete-${category.id}-${this.state.query}`,
                        getProductCollectionParameter: {
                            categoryId: category.id,
                            search: this.state.query,
                            expand: ["properties", "expandEvents"] as any,
                            pageSize: this.props.autoCompleteProductLimit || 5,
                        },
                        disableInventoryRequest: true,
                    } as any);
                }
                this.setState({ selectedCategoryId: category.id, isEvents, focusedAutocompleteItem: undefined });
            }
        }, 350);
    }

    unloadCategoryProducts() {
        clearTimeout(this.hoverTimeoutId);
        this.hoverTimeoutId = setTimeout(() => {
            if (this.state.selectedCategoryId != null) {
                this.setState({ selectedCategoryId: null, isEvents: false, focusedAutocompleteItem: undefined });
            }
        }, 350);
    }

    onMouseEnterProductPanel() {
        clearTimeout(this.hoverTimeoutId);
    }

    renderAutocomplete = () => {
        if (!this.props.autocompleteEnabled) {
            return null;
        }

        if (!this.state.autoCompleteModel) {
            return null;
        }

        const {
            categories,
            brands,
            content,
            trainings,
            suggestions,
            popularBrands,
            popularCategories,
            isRetailSearchCompletionResults,
        } = this.state.autoCompleteModel;
        let productTiles: AutocompleteProductTileModel[] | null = null;
        if (this.state.selectedCategoryId) {
            const selectedCategoryProducts = getProductsForProductInfoList(
                this.props.appState,
                `search-autoComplete-${this.state.selectedCategoryId}-${this.state.query}`,
            );
            if (selectedCategoryProducts) {
                productTiles = [];
                if (selectedCategoryProducts.length > 0) {
                    productTiles = selectedCategoryProducts.map(product => ({
                        productInfoListId: `search-autoComplete-${this.state.selectedCategoryId}-${this.state.query}`,
                        product,
                    })) as AutocompleteProductTileModel[];
                }
            }
        } else {
            productTiles = this.state.autoCompleteModel.productTiles;
        }

        const displayLeftColumn =
            (categories && categories.length > 0) ||
            (trainings && trainings.length > 0) ||
            (brands && brands.length > 0) ||
            (content && content.length > 0);

        const autocompleteProductColumnStyle = displayLeftColumn
            ? styles.autocompleteProductColumn
            : styles.autocompleteFullProductColumn;

        return (
            <>
                {isRetailSearchCompletionResults &&
                    ((suggestions && suggestions.length > 0) ||
                        (categories && categories.length > 0) ||
                        (popularCategories && popularCategories.length > 0) ||
                        (brands && brands.length > 0) ||
                        (popularBrands && popularBrands.length > 0)) && (
                        <StyledWrapper
                            {...this.styles.autocompleteCommerceSearchWrapper}
                            className="autocomplete-commerce-search-wrapper"
                        >
                            {suggestions && suggestions.length > 0 && (
                                <AutocompleteSuggestions
                                    suggestions={suggestions}
                                    focusedItem={this.state.focusedAutocompleteItem}
                                    goToUrl={this.goToUrl}
                                    extendedStyles={this.styles.autocompleteSuggestionsStyles}
                                />
                            )}
                            {((categories && categories.length > 0) ||
                                (popularCategories && popularCategories.length > 0)) && (
                                <AutocompleteCategories
                                    categories={categories}
                                    popularCategories={popularCategories}
                                    focusedItem={this.state.focusedAutocompleteItem}
                                    goToUrl={this.goToUrl}
                                    extendedStyles={this.styles.autocompleteCategoriesStyles}
                                    isRetailSearchCompletionResults={isRetailSearchCompletionResults}
                                    isEvents={false}
                                    loadCategoryProducts={this.loadCategoryProducts}
                                    unloadCategoryProducts={this.unloadCategoryProducts}
                                />
                            )}
                            {((brands && brands.length > 0) || (popularBrands && popularBrands.length > 0)) && (
                                <AutocompleteBrands
                                    brands={brands}
                                    popularBrands={popularBrands}
                                    focusedItem={this.state.focusedAutocompleteItem}
                                    goToUrl={this.goToUrl}
                                    extendedStyles={this.styles.autocompleteBrandsStyles}
                                    isRetailSearchCompletionResults={isRetailSearchCompletionResults}
                                />
                            )}
                        </StyledWrapper>
                    )}
                {!isRetailSearchCompletionResults && (
                    <StyledWrapper {...this.styles.autocompleteWrapper} className="autocomplete-wrapper">
                        {displayLeftColumn && (
                            <StyledWrapper
                                {...this.styles.autocompleteColumnWrapper}
                                className="autocomplete-column-wrapper"
                            >
                                <AutocompleteCategories
                                    categories={categories}
                                    isEvents={false}
                                    categoryListPageUrl={this.props.categoryListPageUrl}
                                    selectedCategoryId={this.state.selectedCategoryId}
                                    focusedItem={this.state.focusedAutocompleteItem}
                                    goToUrl={this.goToUrl}
                                    loadCategoryProducts={this.loadCategoryProducts}
                                    unloadCategoryProducts={this.unloadCategoryProducts}
                                    extendedStyles={this.styles.autocompleteCategoriesStyles}
                                />
                                <AutocompleteCategories
                                    categories={trainings}
                                    isEvents={true}
                                    trainingListPageUrl={this.props.trainingListPageUrl}
                                    selectedCategoryId={this.state.selectedCategoryId}
                                    focusedItem={this.state.focusedAutocompleteItem}
                                    goToUrl={this.goToUrl}
                                    loadCategoryProducts={this.loadCategoryProducts}
                                    unloadCategoryProducts={this.unloadCategoryProducts}
                                    extendedStyles={this.styles.autocompleteCategoriesStyles}
                                />
                                <AutocompleteBrands
                                    brands={brands}
                                    focusedItem={this.state.focusedAutocompleteItem}
                                    goToUrl={this.goToUrl}
                                    extendedStyles={this.styles.autocompleteBrandsStyles}
                                    brandsPageUrl={this.props.brandsPageUrl}
                                />
                                <AutocompleteContent
                                    content={content}
                                    focusedItem={this.state.focusedAutocompleteItem}
                                    doSearch={this.doSearch}
                                    goToUrl={this.goToUrl}
                                    extendedStyles={this.styles.autocompleteContentStyles}
                                />
                            </StyledWrapper>
                        )}

                        <StyledWrapper
                            {...autocompleteProductColumnStyle}
                            onMouseLeave={this.unloadCategoryProducts}
                            onMouseEnter={this.onMouseEnterProductPanel}
                        >
                            {productTiles && productTiles.length > 0 && (
                                <AutocompleteProducts
                                    query={this.state.query}
                                    productTiles={productTiles}
                                    isEvents={this.state.isEvents}
                                    focusedItem={this.state.focusedAutocompleteItem}
                                    goToUrl={this.goToUrl}
                                    extendedStyles={this.styles.autocompleteProductsStyles}
                                    doSearch={this.doSearch}
                                    disableAddToCartActions={this.props.disableAddToCartActions}
                                />
                            )}
                            {productTiles && productTiles.length === 0 && (
                                <>
                                    {this.state.isEvents && (
                                        <Typography {...styles.noResultsMessage}>
                                            {translate("No matching events found.")}
                                        </Typography>
                                    )}
                                    {!this.state.isEvents && (
                                        <Typography {...styles.noResultsMessage}>
                                            {translate("No matching products found.")}
                                        </Typography>
                                    )}
                                </>
                            )}
                            {!productTiles && this.state.selectedCategoryId && (
                                <Typography {...styles.noResultsMessage}>
                                    <EllipsisSpinner
                                        label={translate("Loading")}
                                        showSpinner={true}
                                        typographyProps={styles.noResultsMessage}
                                    />
                                </Typography>
                            )}
                        </StyledWrapper>
                    </StyledWrapper>
                )}
            </>
        );
    };

    render() {
        const styles = this.styles;
        const searchId = `headerSearch-${this.props.id}`;
        const searchInput = (
            <TextField
                {...styles.input}
                ref={this.props.inputRef}
                value={this.state.query}
                onFocus={this.handleInputFocus}
                isAnimated={this.props.isAnimated}
                onChange={this.handleInputChange}
                onKeyDown={this.handlePopoverKeyDown}
                placeholder={translate("Search Product Numbers, Brands or Keywords")}
                autoComplete="off"
                clickableText={translate("submit search")}
                id={searchId}
                data-test-selector="headerSearchInputTextField"
                className="search-input"
            />
        );

        return (
            <StyledWrapper {...styles.wrapper}>
                <VisuallyHidden as="label" htmlFor={searchId} id={`${searchId}-label`}>
                    {translate("Site Search")}
                </VisuallyHidden>
                {searchInput}
                <Button onClick={this.searchClickHandler} {...styles.searchButton}>
                    {translate("search")}
                </Button>
                <Tippy
                    {...tippyProps}
                    onCreate={instance => {
                        this.tippyInstance = instance;
                        this.tippyInstance.popper.style.width = "100%";
                    }}
                    onShow={() => {
                        this.context.registerMenu();
                    }}
                    onHidden={() => {
                        this.context.unRegisterMenu();
                    }}
                    onHide={this.handlePopoverClose}
                    onClickOutside={this.onClickOutside}
                    content={
                        <StyledWrapper {...styles.popoverContent}>
                            <StyledWrapper {...styles.popoverContentBody}>
                                {!this.state.query && this.showAutocomplete && (
                                    <SearchHistory
                                        focusedQuery={this.state.focusedSearchHistoryQuery}
                                        goToUrl={this.goToUrl}
                                        onClearHistory={this.onClearHistory}
                                        extendedStyles={styles.searchHistoryStyles}
                                    />
                                )}
                                {this.state.query && this.renderAutocomplete()}
                                <span id="search-arrow" data-popper-arrow="" className="autocomplete-tippy-arrow" />
                            </StyledWrapper>
                        </StyledWrapper>
                    }
                >
                    <PopoverTrigger></PopoverTrigger>
                </Tippy>
            </StyledWrapper>
        );
    }
}

export default connect(mapStateToProps, mapDispatchToProps)(withHistory(withTheme(SearchInput)));
